import React, { useEffect, useState } from "react";

const AddCompanyType = () => {
    const [data, setData] = useState(null);
    const [newCompanyType, setNewCompanyType] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [editingType, setEditingType] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");

    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

    // Fetch data
    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                setError("");
                const res = await fetch(`${apiBaseUrl}/datacompany`);
                if (!res.ok) throw new Error("Failed to load company types");
                const result = await res.json();
                setData(result);
            } catch (err) {
                console.error("Fetch error:", err);
                setError("Failed to load company types. Please refresh the page.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [apiBaseUrl]);

    const refreshData = async () => {
        try {
            const res = await fetch(`${apiBaseUrl}/datacompany`);
            if (!res.ok) throw new Error("Refresh failed");
            const result = await res.json();
            setData(result);
        } catch (err) {
            alert("Could not refresh. Please reload the page.");
        }
    };

    // Helper to safely get company types array
    const getCompanyTypes = () => {
        try {
            return Array.isArray(data?.states?.[0]?.districts) ? data.states[0].districts : [];
        } catch {
            return [];
        }
    };

    // Add new company type
    const handleAddCompanyType = async () => {
        const trimmed = newCompanyType.trim();
        if (!trimmed) return alert("Please enter a company type");

        const types = getCompanyTypes();
        const lowerTrimmed = trimmed.toLowerCase();

        // Prevent duplicate on add
        if (types.some((t) => t.toLowerCase() === lowerTrimmed)) {
            return alert(`"${trimmed}" already exists!`);
        }

        try {
            const res = await fetch(`${apiBaseUrl}/addcompany`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ district: trimmed }),
            });

            const result = await res.json();
            if (!res.ok) throw new Error(result.error || "Failed to add company type");

            setData(result.data);
            setNewCompanyType("");
            alert("Company type added successfully!");
        } catch (err) {
            alert(err.message || "Failed to add company type");
        }
    };

    // Delete company type
    const handleDeleteCompanyType = async (type) => {
        if (!window.confirm(`Delete "${type}" permanently?`)) return;

        try {
            const res = await fetch(`${apiBaseUrl}/deletecompany`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ district: type }),
            });

            const result = await res.json();
            if (!res.ok) throw new Error(result.error || "Failed to delete");

            setData(result.data);
            alert("Company type deleted successfully!");
        } catch (err) {
            alert(err.message || "Failed to delete company type");
        }
    };

    // Save edited company type
    const handleSaveEdit = async () => {
        const trimmed = editValue.trim();
        if (!trimmed) return alert("Company type cannot be empty");
        if (trimmed === editingType) return cancelEdit();

        const exists = data.states[0].districts.some((t) => t.toLowerCase() === trimmed.toLowerCase() && t !== editingType);
        if (exists) return alert("This company type already exists!");

        setIsSaving(true);
        try {
            const res = await fetch(`${apiBaseUrl}/updatecompany`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    oldDistrict: editingType,
                    newDistrict: trimmed,
                }),
            });

            const result = await res.json();
            if (!res.ok) throw new Error(result.error || "Update failed");

            setData(result.data);
            cancelEdit();
            alert("Company type updated successfully!");
        } catch (err) {
            alert(err.message || "Failed to update company type");
        } finally {
            setIsSaving(false);
        }
    };

    const cancelEdit = () => {
        setEditingType(null);
        setEditValue("");
        setIsSaving(false);
    };

    // Safe extraction
    const companyTypes = (() => {
        try {
            const arr = data?.states?.[0]?.districts;
            return Array.isArray(arr) ? arr : [];
        } catch (e) {
            console.error("Data error:", e);
            return [];
        }
    })();

    const filtered = companyTypes.filter((type) => type.toLowerCase().includes(searchTerm.toLowerCase()));

    if (isLoading) return <p className="text-center py-20 text-gray-600">Loading company types...</p>;
    if (error) return <p className="text-center py-20 text-red-600">{error}</p>;

    return (
        <div className="min-h-screen bg-gray-100 py-8 px-4">
            <div className="max-w-2xl mx-auto bg-white shadow-lg rounded-xl p-6 md:p-8">
                <h1 className="text-3xl font-bold text-center text-gray-800 mb-8">Manage Company Types</h1>

                {/* Add New */}
                <div className="flex flex-col sm:flex-row gap-3 mb-8">
                    <input
                        type="text"
                        value={newCompanyType}
                        onChange={(e) => setNewCompanyType(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleAddCompanyType()}
                        placeholder="Add new company type"
                        className="flex-1 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                        onClick={handleAddCompanyType}
                        className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition"
                    >
                        Add Company Type
                    </button>
                </div>

                {/* Search */}
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search company types..."
                    className="w-full mb-6 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                {/* List */}
                <ul className="space-y-3">
                    {filtered.length === 0 ? (
                        <p className="text-center text-gray-500 py-8">No company types found</p>
                    ) : (
                        filtered.map((type) => (
                            <li
                                key={type}
                                className="flex items-center justify-between bg-gray-50 p-4 rounded-lg border hover:shadow transition"
                            >
                                {editingType === type ? (
                                    <div className="flex items-center gap-2 flex-1">
                                        <input
                                            type="text"
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") handleSaveEdit();
                                                if (e.key === "Escape") cancelEdit();
                                            }}
                                            className="flex-1 px-3 py-2 border rounded focus:outline-none focus:ring"
                                            autoFocus
                                        />
                                        <button
                                            onClick={handleSaveEdit}
                                            disabled={isSaving}
                                            className={`px-4 py-2 text-sm text-white rounded transition ${
                                                isSaving ? "bg-gray-400" : "bg-green-500 hover:bg-green-600"
                                            }`}
                                        >
                                            {isSaving ? "Saving..." : "Save"}
                                        </button>
                                        <button
                                            onClick={cancelEdit}
                                            className="px-4 py-2 text-sm text-white bg-gray-500 rounded hover:bg-gray-600"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <span className="font-medium">{type}</span>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => {
                                                    setEditingType(type);
                                                    setEditValue(type);
                                                }}
                                                className="px-4 py-2 text-sm text-white bg-blue-500 rounded hover:bg-blue-600"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDeleteCompanyType(type)}
                                                className="px-4 py-2 text-sm text-white bg-red-500 rounded hover:bg-red-600"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </>
                                )}
                            </li>
                        ))
                    )}
                </ul>

                {/* Refresh */}
                <div className="text-center mt-8">
                    <button onClick={refreshData} className="text-sm text-gray-600 hover:text-gray-800 underline">
                        Refresh List
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddCompanyType;
